import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import NotificationItem from './NotificationItem';
import type { Notification } from '@/services/notificationsApi';

interface NotificationDropdownProps {
  notifications: Notification[];
  unreadCount: number;
  hasMore: boolean;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onLoadMore: () => void;
  onClose: () => void;
  onOpen: (notification: Notification) => void;
}

export default function NotificationDropdown({
  notifications,
  unreadCount,
  hasMore: _hasMore,
  onMarkRead,
  onMarkAllRead,
  onLoadMore: _onLoadMore,
  onClose,
  onOpen,
}: NotificationDropdownProps) {
  const { t } = useTranslation();

  const visible = notifications.slice(0, 20);

  return (
    <div className="absolute right-0 mt-2 w-96 bg-[var(--bp-surface)] border border-[var(--bp-border)] rounded-xl shadow-xl z-[60] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bp-border-soft)]">
        <h3 className="text-body font-semibold text-[var(--bp-text-1)]">{t('notifications.title')}</h3>
        <button
          onClick={onMarkAllRead}
          disabled={unreadCount === 0}
          className={cn(
            "text-dense transition-colors",
            unreadCount > 0
              ? "text-[var(--bp-accent-text)] hover:text-[var(--bp-accent-text)]"
              : "text-[var(--bp-faint)] cursor-default"
          )}
        >
          {t('notifications.mark_all_read')}
        </button>
      </div>

      {/* List */}
      <div className="max-h-[420px] overflow-y-auto divide-y divide-[var(--bp-border-soft)]">
        {visible.length === 0 ? (
          <p className="px-4 py-8 text-body text-[var(--bp-text-3)] text-center">{t('notifications.empty')}</p>
        ) : (
          visible.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onMarkRead={onMarkRead}
              onOpen={onOpen}
              onClose={onClose}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-[var(--bp-border-soft)] px-4 py-2.5 text-center">
        <Link
          to="/notifications"
          onClick={onClose}
          className="text-dense text-[var(--bp-accent-text)] hover:text-[var(--bp-accent-text)] font-medium transition-colors"
        >
          {t('notifications.view_all')}
        </Link>
      </div>
    </div>
  );
}
